import React from 'react';

import { useStyletron } from 'baseui';
import _map from 'lodash/map';
import _has from 'lodash/has';
import _get from 'lodash/get';
import _join from 'lodash/join';

import Label from '../baseui/input/Label';
import Accordian from '../Accordian';

import InputRenderer from './InputRenderer';
import ColumnRenderer from './ColumnRenderer';
import MultiRowRenderer from './MultiRowRenderer';
import { getCurrentPath, getCurrentDefaultValue } from './helper';

interface IRowRendererProps {
	fieldConfig: any;
	layoutProps: any;
	control: any;
	errors: any;
	setShowLoader: any;
	mode: any;
	watch: any;
	setValue: any;
	path?: any[];
	defaultValue?: any;
	initialValues?: any;
}

const RowRenderer: React.FC<IRowRendererProps> = (props) => {
	const {
		fieldConfig,
		layoutProps,
		path = [],
		control,
		errors,
		mode,
		watch,
		setValue,
		setShowLoader,
		defaultValue,
		initialValues
	} = props;
	const [css] = useStyletron();

	const { key } = layoutProps;

	const renderInput = () => {
		return (
			<InputRenderer
				field={{ ...fieldConfig[key], path }}
				control={control}
				errors={errors}
				setShowLoader={setShowLoader}
				defaultValue={defaultValue}
				mode={mode}
				watch={watch}
				setValue={setValue}
				initialValues={initialValues}
			/>
		);
	};

	const renderColumns = (currentPath: any, currentDefaultValue: any) => {
		return (
			<div
				className={css({
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'space-around',
					gap: '10px'
				})}>
				{_map(layoutProps.getColumns(), (column, index) => {
					return (
						<ColumnRenderer
							key={index}
							path={currentPath}
							layoutProps={column}
							fieldConfig={fieldConfig}
							control={control}
							errors={errors}
							setShowLoader={setShowLoader}
							defaultValue={currentDefaultValue}
							mode={mode}
							watch={watch}
							setValue={setValue}
							initialValues={initialValues}
						/>
					);
				})}
			</div>
		);
	};

	const renderRows = (currentPath: any, currentDefaultValue: any) => {
		return (
			<div
				className={css({
					marginLeft: '50px',
					border: '1px solid #e0e0e0',
					display: 'flex',
					flexDirection: 'column',
					gap: '10px',
					padding: '10px'
				})}>
				{_map(layoutProps.getRows(), (row, index) => {
					return (
						<RowRenderer
							key={`${key}${index}`}
							path={currentPath}
							layoutProps={row}
							fieldConfig={fieldConfig}
							control={control}
							errors={errors}
							setShowLoader={setShowLoader}
							defaultValue={currentDefaultValue}
							mode={mode}
							watch={watch}
							setValue={setValue}
							initialValues={initialValues}
						/>
					);
				})}
			</div>
		);
	};

	const renderMultiRow = (currentPath: any, currentDefaultValue: any) => {
		return (
			<MultiRowRenderer
				path={currentPath}
				layoutProps={layoutProps}
				fieldConfig={fieldConfig}
				control={control}
				errors={errors}
				setShowLoader={setShowLoader}
				defaultValue={currentDefaultValue}
				mode={mode}
				watch={watch}
				setValue={setValue}
				initialValues={initialValues}
			/>
		);
	};

	const renderLayout = (columns: any, rows: any) => {
		let label;
		let isArrayField;
		let currentPath = path;
		let currentDefaultValue = defaultValue;
		let toolTip;
		let required;
		let isCollapsible;
		let description;
		if (_has(fieldConfig, key)) {
			label = fieldConfig[key].getLabel();
			isArrayField = _get(fieldConfig[key].getRenderProps(), 'isArrayField', false);
			currentPath = getCurrentPath(path, fieldConfig[key].getName());
			currentDefaultValue = getCurrentDefaultValue(defaultValue, key);
			toolTip = fieldConfig[key].getToolTip();
			description = fieldConfig[key].getDescription();
			isCollapsible = fieldConfig[key].getIsCollapsible();
			required = _get(fieldConfig[key].getRenderProps(), 'required', false);
		}
		const content = (
			<>
				{isArrayField ? (
					renderMultiRow(currentPath, currentDefaultValue)
				) : (
					<>
						{columns.length ? renderColumns(currentPath, currentDefaultValue) : null}
						{rows.length ? renderRows(currentPath, currentDefaultValue) : null}
					</>
				)}
			</>
		);
		return (
			<div className={label && css({ border: '1px solid #e0e0e0', padding: '10px' })}>
				{isCollapsible ? (
					<Accordian
						{...fieldConfig[key].getRenderProps()}
						watch={watch}
						label={label}
						content={content}
						panelKey={_join(currentPath, '.')}
					/>
				) : (
					<>
						{label && <Label label={label} toolTip={toolTip} required={required} description={description} />}
						{content}
					</>
				)}
			</div>
		);
	};

	const render = () => {
		const columns = layoutProps.getColumns();
		const rows = layoutProps.getRows();
		if (columns.length || rows.length) {
			return renderLayout(columns, rows);
		}
		return renderInput();
	};

	return render();
};

export default RowRenderer;
